"use client";

import { Button } from "@/components/atoms/Button";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  leadCtaClick,
} from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

interface EligibilityCriteriaData {
  id?: number;
  page_id?: number;
  component_id?: number;
  title: string;
  title_text_color?: string;
  description: string;
  description_text_color?: string;
  background_color?: string;
  // Read more link fields
  read_more_text?: string;
  read_more_color?: string;
  // Lead drop button fields
  button_text?: string;
  button_text_color?: string;
  button_color?: string;
  button_icon?: string;
  button_icon_alt?: string;
  variant?: string;
  status?: number;
  is_deleted?: number;
  created_by?: string | null;
  updated_by?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface EligibilityCriteriaProps {
  data: {
    data: EligibilityCriteriaData;
  };
}

function EligibilityCriteria1({ data }: EligibilityCriteriaProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLSpanElement>(null);
  const dispatch = useDispatch();

  const {
    title = "",
    title_text_color = "#FFFFFF",
    description = "",
    description_text_color = "#FFFFFF",
    background_color = "#7f64e3",
    // Read more link
    read_more_text = "",
    read_more_color = "#BE2330",
    // Lead drop button
    button_text = "",
    button_text_color = "#000000",
    button_color = "#FFFFFF",
    button_icon = "",
    button_icon_alt = "",
  } = data?.data || {};

  // Check if text is actually truncated (overflowing)
  useEffect(() => {
    const checkTruncation = () => {
      if (descriptionRef.current && !isExpanded) {
        const element = descriptionRef.current;
        // Check if content is overflowing (scrollHeight > clientHeight means text is truncated)
        setIsTruncated(element.scrollHeight > element.clientHeight);
      }
    };

    checkTruncation();
    // Re-check on window resize as line-clamp behavior changes with screen size
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [description, isExpanded]);

  // Show Read More only if text is actually truncated
  const shouldShowReadMore = isTruncated && !isExpanded;

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "eligibility_criteria_section");
  //   }
  // }, []);

  // Handle CTA button click with tracking
  const handleOpenLeadForm = useCallback(() => {
    leadCtaClick({
      section: "eligibility_criteria_section",
      component: "cta_button",
      label: button_text,
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [dispatch, button_text]);

  return (
    <div
      ref={ref}
      className={cn(
        "px-5 py-8 sm:px-8 sm:py-6 w-full",
        "flex items-center justify-center",
      )}
      style={{ backgroundColor: background_color }}
    >
      <div
        className={cn(
          "flex justify-center items-center flex-col w-full",
          "gap-spacing-14 sm:gap-spacing-20 md:gap-spacing-24",
          "sm:w-[1008px]",
          "md:w-[1070px]",
          "lg:w-[1320px]",
          "xl:w-[1594px]",
        )}
      >
        <h2
          className={cn(
            "text-center",
            "text-heading-6-semibold",
            "sm:text-heading-5-semibold",
            "md:text-heading-4-semibold",
            "lg:text-new-heading-34-semibold",
            "xl:text-new-heading-42-semibold",
            "line-clamp-1",
            "type-h3",
          )}
          style={{ color: title_text_color }}
        >
          {title}
        </h2>
        <div
          className={cn(
            "text-center w-full relative",
            "text-body-2-regular",
            "sm:text-body-3-regular",
            "md:text-body-2-regular",
            "lg:text-body-1-regular",
            "xl:text-heading-6-regular",
            "type-body-sm",
          )}
          style={{ color: description_text_color }}
        >
          <span
            ref={descriptionRef}
            className={cn(!isExpanded && "line-clamp-4 sm:line-clamp-1")}
          >
            {description}
          </span>
          {shouldShowReadMore && (
            <span
              className="absolute bottom-0 right-0 pl-1"
              style={{ backgroundColor: background_color }}
            >
              {"... "}
              <button
                type="button"
                className={cn(
                  "inline cursor-pointer",
                  "text-body-2-bold",
                  "sm:text-body-3-bold",
                  "md:text-body-2-bold",
                  "lg:text-body-1-bold",
                  "xl:text-heading-6-bold",
                  "border-none bg-transparent p-0 m-0 font-inherit underline",
                )}
                style={{ color: read_more_color }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(true);
                }}
              >
                {read_more_text}
              </button>
            </span>
          )}
        </div>

        {button_text && (
          <Button
            classType="ghost"
            onClick={handleOpenLeadForm}
            className={cn(
              "flex items-center justify-center gap-2 mt-2",
              "xs:text-heading-9-semibold xs:px-4 xs:py-2",
              "sm:text-heading-8-semibold sm:px-5 sm:py-[10px]",
              "md:text-body-1-semibold md:px-6 md:py-3",
              "lg:text-new-body-24-semibold lg:px-7 lg:py-[14px]",
              "rounded-lg transition-all duration-200 hover:opacity-90",
            )}
            style={{
              backgroundColor: button_color,
              color: button_text_color,
            }}
          >
            <span>{button_text}</span>
            {button_icon && (
              <Image
                src={button_icon}
                alt={button_icon_alt}
                width={24}
                height={24}
                className={cn(
                  "xs:w-5 xs:h-5",
                  "sm:w-5 sm:h-5",
                  "md:w-6 md:h-6",
                  "lg:w-6 lg:h-6",
                )}
              />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

export default EligibilityCriteria1;
